import { ChangeDetectionStrategy, Component, DestroyRef, inject, viewChild } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { AsyncPipe } from '@angular/common';
import { LegacyHealthCheckService, StorageService } from '@coin/shared/core/data-access';
import { ContentLanguage, StorageKey } from '@coin/shared/core/util-enums';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, catchError, combineLatest, map, Observable, throwError } from 'rxjs';
import { AuthService } from '@coin/shared/auth/data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UpdateNotificationsService } from '@coin/shared/core/util-helpers';
import { Topic } from '@coin/shared/core/util-models';
import { BannerState } from './home/state/banner.state';
import { SidebarComponent } from './static/sidebar/sidebar.component';
import { HeaderComponent } from './static/header/header.component';
import { MaintenanceBannerComponent } from './shared/components/maintenance-banner/maintenance-banner.component';

@Component({
  selector: 'cosmos-app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [HeaderComponent, MaintenanceBannerComponent, RouterOutlet, SidebarComponent, AsyncPipe]
})
export class AppComponent {
  sidebarComponent = viewChild(SidebarComponent);

  private readonly translateService = inject(TranslateService);
  private readonly storageService = inject(StorageService);
  private readonly authService = inject(AuthService);
  private readonly bannerState = inject(BannerState);
  private readonly healthCheckService = inject(LegacyHealthCheckService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  public isHeaderVisible = false;
  public bannerTopics = this.bannerState.visibleBannerTopics;

  public healthCheckFail$ = new BehaviorSubject<boolean>(false);
  public isSideBarVisible$: Observable<boolean> = combineLatest([
    this.healthCheckFail$,
    this.authService.isAuthenticated$,
    this.authService.isAuthorized$
  ]).pipe(
    map(([healthCheckFail, isAuthenticated, isAuthorized]) => !healthCheckFail && isAuthenticated && isAuthorized)
  );

  public get sideBarCollapsed$(): Observable<boolean> {
    return this.sidebarComponent()!.sideBarCollapsed$;
  }

  constructor() {
    inject(UpdateNotificationsService).checkForUpdates();
    this.translateService.setDefaultLang(ContentLanguage.ENGLISH);
    this.setupLang();
    this.checkBackendStatus();
    this.listenToAuthorizedStatus();
    this.isHeaderVisible = !window.location.href.includes('/login');
  }

  private listenToAuthorizedStatus(): void {
    this.authService.isAuthorized$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(isAuthorized => {
      if (!isAuthorized) {
        this.router.navigate(['unauthorized']);
      }
    });
  }

  private setupLang(): void {
    const lang = this.storageService.get(StorageKey.LANGUAGE);

    if (lang) {
      this.translateService.reloadLang(lang);
    }

    this.translateService.use(lang ?? ContentLanguage.ENGLISH);
  }

  private checkBackendStatus(): void {
    this.healthCheckService
      .ping()
      .pipe(
        catchError(err => {
          this.healthCheckFail$.next(true);
          this.router.navigate(['fallback'], { skipLocationChange: true });
          return throwError(() => err);
        })
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  public closeBanner(topic: Topic): void {
    this.bannerState.updateBannerVisibility(topic);
  }
}
